/**
 * AdminTeamPanel — super-admin-only roster of the 4 staff sign-in accounts.
 *
 * Capabilities:
 *   - Lists Super, Systems, Services, Academy admins (live from DB)
 *   - Per-row "Reset password" → mints a random 20-char password,
 *     bumps session_version (boots active sessions), shows the temp
 *     password ONCE so admin can copy out-of-band
 *   - Self change-password card uses the standard /api/auth/change-password
 *
 * Mounted under /admin → "Admin Team" tab (only visible when scope.canManageUsers).
 *
 * Backend mirrors the same role check — /api/academy/admin/admin-team
 * returns 403 to division admins, so this panel can never be reached
 * by a non-super.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ShieldCheck, KeyRound, RefreshCw, Copy, Check, AlertTriangle, Lock,
  ExternalLink, Crown, Hammer, ShoppingBag, GraduationCap, Mail, X,
  History, User, ArrowRight, Download,
} from 'lucide-react';

const API = `${(process.env.REACT_APP_API_BASE_URL || process.env.CRA_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

// CSV exporter for the audit drawer. Quotes every field per RFC 4180 so
// commas, newlines and quotes inside metadata_json don't break the file.
// Triggers a Blob download with a filename tagged by filter key + ISO ts.
function exportAuditRowsToCSV(rows, filterKey) {
  const headers = ['ts', 'action', 'actor_email', 'target', 'target_kind', 'metadata_json'];
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const lines = [headers.map(esc).join(',')];
  for (const r of rows || []) {
    lines.push([
      esc(r.ts || ''),
      esc(r.action || ''),
      esc(r.actor_email || ''),
      esc(r.target || ''),
      esc(r.target_kind || ''),
      esc(JSON.stringify(r.metadata || {})),
    ].join(','));
  }
  const csv = lines.join('\n') + '\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-${filterKey || 'all'}-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const DIVISION_META = {
  super:    { Icon: Crown,          tone: 'bg-zinc-950 text-[#f5a623]',         tagline: 'Full access · all divisions · user management' },
  systems:  { Icon: Hammer,         tone: 'bg-orange-100 text-orange-800',      tagline: 'Systems leads · bookings · custom builds' },
  services: { Icon: ShoppingBag,    tone: 'bg-amber-100 text-amber-800',        tagline: 'E-store · drip · Stripe · refunds' },
  academy:  { Icon: GraduationCap,  tone: 'bg-purple-100 text-purple-800',      tagline: 'LMS · students · transcripts · M365' },
};

// Tab definitions for the audit-trail drawer. Adding a new tab is a one-line
// change: append { key, label, actions } and the drawer + backend filter
// pick it up automatically.
const AUDIT_FILTERS = [
  { key: 'resets',   label: 'Password resets', actions: ['admin_team.password_reset', 'user.password_reset', 'onboarding.resend'] },
  { key: 'users',    label: 'User mgmt',       actions: ['user.create', 'user.delete', 'user.revoke_access', 'user.restore_access'] },
  { key: 'security', label: 'Security',        actions: ['admin.2fa_enable', 'admin.2fa_disable', 'admin.2fa_recovery_regenerate', 'admin.sign_out_everywhere', 'admin_team.2fa_reset'] },
  { key: 'm365',     label: 'Microsoft 365',   actions: ['m365.grant', 'm365.revoke', 'm365.bulk_grant'] },
  { key: 'all',      label: 'All recent',      actions: [] },
];

// Map raw action keys to a friendly label + icon hint. Used by AuditRow so
// that, regardless of the active filter tab, every entry reads cleanly
// without leaking dotted-namespaced strings into the UI.
const ACTION_LABELS = {
  'admin_team.password_reset':     'Reset admin password',
  'user.password_reset':           'Reset user password',
  'onboarding.resend':             'Resent onboarding email',
  'user.create':                   'Created user',
  'user.delete':                   'Deleted user',
  'user.revoke_access':            'Revoked access',
  'user.restore_access':           'Restored access',
  'admin.2fa_enable':              'Enabled 2FA',
  'admin.2fa_disable':             'Disabled 2FA',
  'admin.2fa_recovery_regenerate': 'Regenerated 2FA recovery codes',
  'admin.sign_out_everywhere':     'Signed out everywhere',
  'admin_team.2fa_reset':          'Reset admin 2FA',
  'm365.grant':                    'Granted M365 apps',
  'm365.revoke':                   'Revoked M365 apps',
  'm365.bulk_grant':               'Bulk-granted M365 apps',
  'demo.share':                    'Shared demo credentials',
};

function Pill({ children, tone = 'bg-gray-100 text-gray-700' }) {
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.16em] ${tone}`}>{children}</span>;
}

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); }
        catch { /* ignore */ }
      }}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-gray-200 hover:border-[#c9a227] text-[10.5px] font-bold uppercase tracking-wider text-gray-600 hover:text-[#a8871a] transition-colors"
    >
      {copied ? <Check className="w-3 h-3 text-emerald-700" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : label}
    </button>
  );
}

function StaffRow({ row, onReset, onReset2FA, busyDivision, busyDivision2FA, openConfirmFor, isOpen }) {
  const meta = DIVISION_META[row.division] || DIVISION_META.academy;
  const Icon = meta.Icon;
  const busy = busyDivision === row.division;
  const busy2FA = busyDivision2FA === row.division;

  return (
    <div
      className="rounded-xl border border-gray-200 p-4 bg-white"
      data-testid={`admin-team-row-${row.division}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${meta.tone}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-[14px] font-bold text-gray-900">{row.label}</h4>
              {!row.exists && <Pill tone="bg-rose-100 text-rose-800">Not seeded</Pill>}
              {row.disabled && <Pill tone="bg-rose-100 text-rose-800">Disabled</Pill>}
              {row.totp_enabled && <Pill tone="bg-emerald-100 text-emerald-800">2FA on</Pill>}
            </div>
            <p className="text-[10.5px] text-gray-500 truncate">{meta.tagline}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {row.totp_enabled && (
            <button
              type="button"
              onClick={() => onReset2FA(row)}
              disabled={busy2FA || !row.exists}
              data-testid={`admin-team-reset2fa-${row.division}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border-2 border-zinc-300 hover:border-[#c9a227] text-zinc-700 font-bold uppercase tracking-wider text-[10.5px] disabled:opacity-40 transition-colors"
              title="Wipe TOTP enrollment — target self-enrols on next login"
            >
              {busy2FA ? <RefreshCw className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
              {busy2FA ? 'Resetting…' : 'Reset 2FA'}
            </button>
          )}
          <button
            type="button"
            onClick={() => openConfirmFor(row)}
            disabled={busy || !row.exists}
            data-testid={`admin-team-reset-${row.division}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-zinc-950 font-bold uppercase tracking-wider text-[10.5px] disabled:opacity-40 hover:opacity-90 transition-opacity"
            style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}
            title="Mint a new random password — optionally email a copy to a recovery inbox"
          >
          {busy ? <RefreshCw className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />}
          {busy ? 'Resetting…' : 'Reset password'}
        </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-[11.5px]">
        <span className="text-gray-500 font-bold uppercase tracking-wider text-[9.5px]">Login URL</span>
        <div className="flex items-center gap-1.5 min-w-0">
          <code className="font-mono text-[10.5px] text-gray-800 truncate">{row.login_url}</code>
          <a href={row.login_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#c9a227]" title="Open in new tab">
            <ExternalLink className="w-3 h-3" />
          </a>
          <CopyButton text={row.login_url} label="" />
        </div>

        <span className="text-gray-500 font-bold uppercase tracking-wider text-[9.5px]">Email</span>
        <div className="flex items-center gap-1.5 min-w-0">
          <code className="font-mono text-[10.5px] text-gray-800 truncate">{row.email}</code>
          <CopyButton text={row.email} label="" />
        </div>

        <span className="text-gray-500 font-bold uppercase tracking-wider text-[9.5px]">Last login</span>
        <span className="text-gray-700">{row.last_login_at ? new Date(row.last_login_at).toLocaleString() : 'Never'}</span>
      </div>

      {isOpen && (
        <ResetConfirmCard row={row} onCancel={() => openConfirmFor(null)} onConfirm={onReset} />
      )}
    </div>
  );
}

function ResetConfirmCard({ row, onCancel, onConfirm }) {
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [sendCopy, setSendCopy] = useState(false);
  const [emailErr, setEmailErr] = useState('');

  const submit = (e) => {
    e.preventDefault();
    let inbox = '';
    if (sendCopy) {
      const v = recoveryEmail.trim().toLowerCase();
      if (!v || !v.includes('@') || v.length > 320) {
        setEmailErr('Enter a valid email address');
        return;
      }
      inbox = v;
    }
    onConfirm(row, inbox);
  };

  return (
    <form
      onSubmit={submit}
      data-testid={`admin-team-confirm-${row.division}`}
      className="mt-3 rounded-lg border-2 border-[#c9a227] bg-[#fffbe9] p-3 flex flex-col gap-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11.5px] text-gray-700 leading-relaxed">
          <strong>Confirm reset for {row.label}.</strong> This mints a fresh 20-char password,
          boots all active sessions, and shows the password ONCE.
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-700 shrink-0"
          aria-label="Cancel"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-gray-700">
        <input
          type="checkbox"
          checked={sendCopy}
          onChange={(e) => { setSendCopy(e.target.checked); setEmailErr(''); }}
          data-testid={`admin-team-email-toggle-${row.division}`}
          className="accent-[#c9a227]"
        />
        <Mail className="w-3 h-3" /> Email a copy to a recovery inbox
      </label>

      {sendCopy && (
        <div className="flex flex-col gap-1">
          <input
            type="email"
            value={recoveryEmail}
            onChange={(e) => { setRecoveryEmail(e.target.value); setEmailErr(''); }}
            placeholder="recovery@yourcompany.com"
            data-testid={`admin-team-email-input-${row.division}`}
            className="px-3 py-2 rounded-md border border-gray-300 text-[12.5px] focus:outline-none focus:border-[#c9a227]"
          />
          {emailErr && (
            <span className="text-[10.5px] font-bold text-rose-700">
              <AlertTriangle className="w-3 h-3 inline mr-1" />{emailErr}
            </span>
          )}
          <span className="text-[10px] text-gray-500">
            Email is sent via Resend. Triggers an audit-log entry with the recipient.
          </span>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider text-gray-600 hover:text-gray-900"
          data-testid={`admin-team-confirm-cancel-${row.division}`}
        >
          Cancel
        </button>
        <button
          type="submit"
          data-testid={`admin-team-confirm-submit-${row.division}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-zinc-950 font-bold uppercase tracking-wider text-[10.5px] hover:opacity-90"
          style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}
        >
          <KeyRound className="w-3 h-3" /> {sendCopy ? 'Reset & email' : 'Reset password'}
        </button>
      </div>
    </form>
  );
}

export default function AdminTeamPanel({ scope }) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busyDivision, setBusyDivision] = useState(null);
  const [busyDivision2FA, setBusyDivision2FA] = useState(null);
  const [revealed, setRevealed] = useState(null); // { division, email, temp_password, emailed_to }
  const [confirmFor, setConfirmFor] = useState(null); // division key currently showing the confirm card

  // Audit-trail drawer
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState(null); // null = not loaded, [] = empty, [...] = rows
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditErr, setAuditErr] = useState(null);
  const [auditFilter, setAuditFilter] = useState('resets');

  // Self change-password state
  const [cur, setCur] = useState('');
  const [next1, setNext1] = useState('');
  const [next2, setNext2] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${API}/academy/admin/admin-team`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setTeam(d.team || []);
    } catch (e) { setErr(e.message || 'Load failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadAudit = useCallback(async (filterKey = 'resets') => {
    setAuditLoading(true); setAuditErr(null); setAuditFilter(filterKey);
    try {
      const filter = AUDIT_FILTERS.find((f) => f.key === filterKey) || AUDIT_FILTERS[0];
      const params = new URLSearchParams({ limit: '50' });
      if (filter.actions && filter.actions.length) {
        params.set('actions', filter.actions.join(','));
      }
      const r = await fetch(`${API}/academy/admin/audit-log?${params.toString()}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setAuditRows(d.rows || []);
    } catch (e) { setAuditErr(e.message || 'Failed to load audit trail'); }
    finally { setAuditLoading(false); }
  }, []);

  const openAudit = () => { setAuditOpen(true); loadAudit('resets'); };

  const onReset = async (row, recoveryEmail = '') => {
    setBusyDivision(row.division); setRevealed(null); setConfirmFor(null);
    try {
      const r = await fetch(`${API}/academy/admin/admin-team/${row.division}/reset-password`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(recoveryEmail ? { recovery_email: recoveryEmail } : {}),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRevealed(d);
      await load(); // refresh last_login etc.
    } catch (e) {
      setErr(e.message || 'Reset failed');
    } finally { setBusyDivision(null); }
  };

  const onChangeMyPassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (next1.length < 12) { setPwMsg({ ok: false, text: 'New password must be at least 12 characters' }); return; }
    if (next1 !== next2) { setPwMsg({ ok: false, text: 'New passwords don\'t match' }); return; }
    setPwBusy(true);
    try {
      const r = await fetch(`${API}/auth/change-password`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: cur, new_password: next1 }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setPwMsg({ ok: true, text: 'Password changed ✓ All your other sessions have been signed out.' });
      setCur(''); setNext1(''); setNext2('');
    } catch (e) { setPwMsg({ ok: false, text: e.message || 'Change failed' }); }
    finally { setPwBusy(false); }
  };

  return (
    <section data-testid="admin-team-panel" className="flex flex-col gap-4">
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <header className="px-6 py-4 flex items-center gap-2 bg-gradient-to-r from-zinc-950 to-zinc-800 text-white">
          <ShieldCheck className="w-4 h-4 text-[#f5a623]" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#f5a623]">Security · Admin team</div>
            <h3 className="text-[15px] font-bold">Staff accounts &amp; credentials</h3>
            <p className="text-[11px] text-white/60 mt-0.5">
              Super-admin can mint new temporary passwords for any of the 4 staff accounts. Resets boot active sessions.
            </p>
          </div>
          <button
            type="button"
            onClick={openAudit}
            data-testid="admin-team-audit-open"
            className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-[10.5px] font-bold uppercase tracking-wider text-white transition-colors"
            title="View the last 20 password resets across the admin team"
          >
            <History className="w-3 h-3" /> Audit trail
          </button>
        </header>

        <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="admin-team-list">
          {loading && <div className="text-[12px] text-gray-500 p-4">Loading team…</div>}
          {err && (
            <div className="col-span-full px-3 py-2 rounded-md text-[11.5px] bg-rose-50 text-rose-800 border border-rose-200">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {err}
            </div>
          )}
          {team.map((row) => (
            <StaffRow
              key={row.division}
              row={row}
              onReset={onReset}
              onReset2FA={onReset2FA}
              busyDivision={busyDivision}
              busyDivision2FA={busyDivision2FA}
              openConfirmFor={(r) => setConfirmFor(r ? r.division : null)}
              isOpen={confirmFor === row.division}
            />
          ))}
        </div>

        {revealed && (
          <div className="mx-4 mb-4 rounded-xl border-2 border-[#c9a227] bg-[#fffbe9] p-4" data-testid="admin-team-reveal">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">Copy now — shown ONCE</div>
                <h4 className="text-[14px] font-bold text-gray-900">{revealed.email}</h4>
                <p className="text-[10.5px] text-gray-600 mt-1">{revealed.warning}</p>
              </div>
              <button type="button" onClick={() => setRevealed(null)} className="text-gray-400 hover:text-gray-700 text-[10px] font-bold uppercase tracking-wider">Dismiss</button>
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <code className="font-mono text-[14px] font-bold text-[#0e0e10] bg-white px-3 py-2 rounded-md border-2 border-[#c9a227]">{revealed.temp_password}</code>
              <CopyButton text={revealed.temp_password} label="Copy password" />
            </div>
            {revealed.emailed_to && (
              <div
                data-testid="admin-team-emailed-to"
                className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[10.5px] font-bold text-emerald-800"
              >
                <Mail className="w-3 h-3" />
                Recovery copy sent to <code className="font-mono ml-0.5">{revealed.emailed_to}</code>
                <Check className="w-3 h-3" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Self change-password */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <header className="px-6 py-4 flex items-center gap-2 bg-[#fffbe9] border-b border-[#f0e3a8]">
          <Lock className="w-4 h-4 text-[#a8871a]" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">Personal · Account security</div>
            <h3 className="text-[14px] font-bold text-gray-900">Change my password</h3>
            <p className="text-[10.5px] text-gray-500 mt-0.5">Signed in as {scope?.email || 'admin'}</p>
          </div>
        </header>
        <form onSubmit={onChangeMyPassword} className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-team-change-mine">
          <input type="password" required value={cur}   onChange={(e) => setCur(e.target.value)}   placeholder="Current password" className="px-3 py-2 rounded-md border border-gray-200 text-[13px] focus:outline-none focus:border-[#c9a227]" data-testid="admin-team-change-current" />
          <input type="password" required value={next1} onChange={(e) => setNext1(e.target.value)} placeholder="New password (12+ chars)" className="px-3 py-2 rounded-md border border-gray-200 text-[13px] focus:outline-none focus:border-[#c9a227]" data-testid="admin-team-change-new1" />
          <input type="password" required value={next2} onChange={(e) => setNext2(e.target.value)} placeholder="Confirm new password" className="px-3 py-2 rounded-md border border-gray-200 text-[13px] focus:outline-none focus:border-[#c9a227]" data-testid="admin-team-change-new2" />
          <div className="md:col-span-3 flex items-center justify-between gap-2 flex-wrap">
            {pwMsg && (
              <span className={`text-[11.5px] font-bold ${pwMsg.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
                {pwMsg.ok ? <Check className="w-3 h-3 inline mr-1" /> : <AlertTriangle className="w-3 h-3 inline mr-1" />}
                {pwMsg.text}
              </span>
            )}
            <button type="submit" disabled={pwBusy} data-testid="admin-team-change-submit" className="ml-auto inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-zinc-950 font-bold uppercase tracking-wider text-[10.5px] disabled:opacity-40 hover:opacity-90 transition-opacity" style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
              <KeyRound className="w-3 h-3" /> {pwBusy ? 'Changing…' : 'Change password'}
            </button>
          </div>
        </form>
      </div>

      {/* Audit-trail drawer */}
      {auditOpen && (
        <AuditTrailDrawer
          rows={auditRows}
          loading={auditLoading}
          err={auditErr}
          activeFilter={auditFilter}
          onClose={() => setAuditOpen(false)}
          onReload={() => loadAudit(auditFilter)}
          onChangeFilter={(k) => loadAudit(k)}
        />
      )}
    </section>
  );
}

function AuditTrailDrawer({ rows, loading, err, activeFilter, onClose, onReload, onChangeFilter }) {
  // Close on Escape — small touch, no listeners stacking thanks to useEffect cleanup.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const activeLabel = (AUDIT_FILTERS.find((f) => f.key === activeFilter) || AUDIT_FILTERS[0]).label;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      data-testid="admin-team-audit-drawer"
      role="dialog"
      aria-modal="true"
    >
      {/* backdrop */}
      <button
        type="button"
        aria-label="Close audit trail"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px] cursor-default"
        data-testid="admin-team-audit-backdrop"
      />
      {/* panel */}
      <div className="relative w-full max-w-[560px] h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
        <header className="px-5 py-4 flex items-center gap-2 bg-gradient-to-r from-zinc-950 to-zinc-800 text-white">
          <History className="w-4 h-4 text-[#f5a623]" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#f5a623]">Security · Audit trail</div>
            <h3 className="text-[14px] font-bold">{activeLabel}</h3>
          </div>
          <button
            type="button"
            onClick={() => exportAuditRowsToCSV(rows || [], activeFilter)}
            disabled={!rows || rows.length === 0}
            data-testid="admin-team-audit-export"
            className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider text-white/80 hover:text-white hover:bg-white/10 disabled:opacity-40 inline-flex items-center gap-1"
            title="Download the rows currently shown as CSV"
          >
            <Download className="w-3 h-3" /> CSV
          </button>
          <button
            type="button"
            onClick={onReload}
            disabled={loading}
            data-testid="admin-team-audit-reload"
            className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider text-white/80 hover:text-white hover:bg-white/10 disabled:opacity-40"
            title="Reload"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-team-audit-close"
            className="ml-1 p-1 rounded-md text-white/70 hover:text-white hover:bg-white/10"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Filter tabs */}
        <div
          className="border-b border-gray-200 bg-gray-50 px-2 flex items-center gap-1 overflow-x-auto"
          data-testid="admin-team-audit-tabs"
        >
          {AUDIT_FILTERS.map((f) => {
            const active = f.key === activeFilter;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => onChangeFilter(f.key)}
                data-testid={`admin-team-audit-tab-${f.key}`}
                className={`shrink-0 px-3 py-2 text-[10.5px] font-bold uppercase tracking-wider border-b-2 transition-colors ${
                  active
                    ? 'border-[#c9a227] text-[#a8871a]'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2" data-testid="admin-team-audit-list">
          {loading && rows === null && <div className="text-[12px] text-gray-500 p-2">Loading…</div>}
          {err && (
            <div className="px-3 py-2 rounded-md text-[11.5px] bg-rose-50 text-rose-800 border border-rose-200">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {err}
            </div>
          )}
          {!loading && !err && rows && rows.length === 0 && (
            <div className="text-[12px] text-gray-500 p-2" data-testid="admin-team-audit-empty">
              No entries recorded for this filter yet.
            </div>
          )}
          {rows && rows.map((r) => (
            <AuditRow key={r.id} row={r} />
          ))}
        </div>
        <footer className="px-5 py-3 bg-gray-50 border-t border-gray-200 text-[10.5px] text-gray-500">
          Showing the most recent {rows?.length ?? 0} entries. Older rows remain in the
          <code className="font-mono mx-1">admin_audit</code> collection.
        </footer>
      </div>
    </div>
  );
}

function AuditRow({ row }) {
  const when = row.ts ? new Date(row.ts).toLocaleString() : '—';
  const meta = row.metadata || {};
  const division = meta.division || '';
  const divisionTone =
    division === 'super'    ? 'bg-zinc-950 text-[#f5a623]' :
    division === 'systems'  ? 'bg-orange-100 text-orange-800' :
    division === 'services' ? 'bg-amber-100 text-amber-800' :
    division === 'academy'  ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-700';
  const friendly = ACTION_LABELS[row.action] || row.action || 'Audit event';
  const tone =
    row.action === 'user.delete' || row.action === 'user.revoke_access' || row.action === 'admin.2fa_disable'
      ? 'bg-rose-50 border-rose-200'
      : row.action === 'user.create' || row.action === 'user.restore_access' || row.action === 'admin.2fa_enable'
        ? 'bg-emerald-50 border-emerald-200'
        : 'bg-white border-gray-200';

  // Action-specific extras (recovery email, M365 apps, role).
  const recoveryEmail = meta.emailed_to;
  const apps = Array.isArray(meta.apps) ? meta.apps : null;
  const targetRole = meta.role;

  return (
    <div
      className={`rounded-lg border p-3 flex flex-col gap-1.5 ${tone}`}
      data-testid={`admin-team-audit-row-${row.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap text-[11.5px]">
        <span className="px-2 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.16em] bg-zinc-950 text-[#f5a623]">
          {friendly}
        </span>
        {division && (
          <span className={`px-2 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.16em] ${divisionTone}`}>
            {division}
          </span>
        )}
        {targetRole && (
          <span className="px-2 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.16em] bg-gray-100 text-gray-700">
            {targetRole}
          </span>
        )}
        <span className="text-gray-500">{when}</span>
      </div>
      <div className="flex items-center gap-1.5 text-[12px] text-gray-800 flex-wrap">
        <User className="w-3 h-3 text-gray-400" />
        <code className="font-mono text-[11px]">{row.actor_email || '—'}</code>
        {row.target && (
          <>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <code className="font-mono text-[11px]">{row.target}</code>
          </>
        )}
      </div>
      {recoveryEmail && (
        <div
          className="inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-bold text-emerald-800"
          data-testid={`admin-team-audit-emailed-${row.id}`}
        >
          <Mail className="w-3 h-3" />
          Recovery copy → <code className="font-mono">{recoveryEmail}</code>
        </div>
      )}
      {apps && apps.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {apps.map((a) => (
            <span key={a} className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[9.5px] font-bold uppercase tracking-wider">{a}</span>
          ))}
        </div>
      )}
    </div>
  );
}
